// UplotBase.tsx — generic responsive uPlot host. Handles instantiation, ResizeObserver
// driven width updates (uPlot itself has no built-in responsive mode), data updates and
// teardown. Concrete charts (SimulationChart, CompareChart) only build `options`/`data`.
import { useEffect, useRef } from 'preact/hooks';
import uPlot from 'uplot';
import type { AlignedData, Options } from 'uplot';

export interface UplotBaseProps {
  data: AlignedData;
  buildOptions: (width: number) => Options;
  height: number;
}

export function UplotBase({ data, buildOptions, height }: UplotBaseProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const width = host.clientWidth || 320;
    const plot = new uPlot(buildOptions(width), data, host);
    plotRef.current = plot;
    // Exposes the live instance on its own host node (uPlot itself never does this) so
    // the current scale ranges (u.scales.x/y) can be read from outside React/Preact —
    // browser devtools, or a Playwright E2E check that a drag/wheel/pinch zoom actually
    // narrowed both axes and that "Zoom zurücksetzen" restores them. No behavioural
    // effect of its own; just a reference, cleaned up alongside the instance below.
    (host as HTMLDivElement & { __uplot?: uPlot }).__uplot = plot;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 10) plot.setSize({ width: w, height });
    });
    ro.observe(host);

    return () => {
      ro.disconnect();
      plot.destroy();
      plotRef.current = null;
      delete (host as HTMLDivElement & { __uplot?: uPlot }).__uplot;
    };
    // Intentionally re-created only when the options builder identity changes (callers
    // memoise/rebuild it when the underlying result changes); data updates are pushed
    // via setData below without a full re-instantiation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildOptions]);

  useEffect(() => {
    plotRef.current?.setData(data);
  }, [data]);

  return <div class="chart-canvas-host" ref={hostRef} />;
}
